import { useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase-client';
import { loadActiveProfile } from './profile-loader';
import { AuthContext } from './auth-context';
import { getSessionWithTimeout, REAUTH_REQUIRED_EVENT } from './session-guard';
import type {
  AuthContextValue,
  AuthStatus,
  ResetPasswordResult,
  SignInErrorCode,
  SignInResult,
} from './types';

/**
 * AuthProvider mounts at the root of the app and resolves Supabase auth state
 * into the four-state AuthStatus model. Consumers read state via the useAuth
 * hook (from `./use-auth`).
 *
 * This file deliberately exports ONLY the AuthProvider component, so that
 * react-refresh/only-export-components is satisfied. The context object and
 * the hooks live in `./auth-context` and `./use-auth` respectively.
 */

/**
 * Map Supabase auth error messages to typed SignInErrorCode values.
 * Supabase doesn't expose stable error codes for password auth, so we
 * pattern-match on the message text. This keeps UI copy stable even if
 * Supabase tweaks their messages.
 */
function classifySignInError(message: string): SignInErrorCode {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials') || m.includes('invalid email or password')) {
    return 'invalid_credentials';
  }
  if (m.includes('email not confirmed')) {
    return 'email_not_confirmed';
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'rate_limited';
  }
  if (m.includes('network') || m.includes('fetch')) {
    return 'network';
  }
  return 'unknown';
}

function userMessageForCode(code: SignInErrorCode): string {
  switch (code) {
    case 'invalid_credentials':
      return 'Email or password is incorrect.';
    case 'email_not_confirmed':
      return 'Please confirm your email address before signing in.';
    case 'rate_limited':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'network':
      return 'Network error. Please check your connection and try again.';
    case 'unknown':
      return 'Unable to sign in. Please try again or contact support.';
  }
}

/**
 * Resolve a Supabase session into the four-state AuthStatus. Centralizing
 * this here means the initial getSession probe and the onAuthStateChange
 * listener both go through the same path.
 */
async function resolveStatus(session: Session | null): Promise<AuthStatus> {
  if (!session?.user) {
    return { status: 'unauthenticated' };
  }
  const profile = await loadActiveProfile(session.user.id);
  if (!profile) {
    return { status: 'unprovisioned', user: session.user, session };
  }
  return { status: 'ready', user: session.user, session, profile };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthStatus>({ status: 'loading' });

  useEffect(() => {
    let mounted = true;
    let resolveSeq = 0;

    const resolveAndSet = (session: Session | null) => {
      const seq = ++resolveSeq;
      void resolveStatus(session)
        .then((next) => {
          if (mounted && seq === resolveSeq) setAuth(next);
        })
        .catch(() => {
          if (mounted && seq === resolveSeq) setAuth({ status: 'unauthenticated' });
        });
    };

    const onReauthRequired = () => {
      if (mounted) setAuth({ status: 'unauthenticated' });
    };
    window.addEventListener(REAUTH_REQUIRED_EVENT, onReauthRequired);

    // Initial session probe. Resolves to one of the four states.
    getSessionWithTimeout()
      .then((session) => {
        if (mounted) resolveAndSet(session);
      })
      .catch(() => {
        if (mounted) setAuth({ status: 'unauthenticated' });
      });

    // Listen for sign-in / sign-out events. Supabase fires onAuthStateChange
    // on token refresh too. Keep the callback synchronous: nested Supabase
    // calls inside this callback can block auth locks after idle tab resume.
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        window.setTimeout(() => {
          if (mounted) resolveAndSet(session);
        }, 0);
      },
    );

    return () => {
      mounted = false;
      window.removeEventListener(REAUTH_REQUIRED_EVENT, onReauthRequired);
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signInWithPassword = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) {
          const code = classifySignInError(error.message);
          return { ok: false, code, message: userMessageForCode(code) };
        }
        // onAuthStateChange listener will resolve to 'ready' or 'unprovisioned'.
        return { ok: true };
      } catch (e) {
        const message = e instanceof Error ? e.message : 'unknown';
        const code = classifySignInError(message);
        return { ok: false, code, message: userMessageForCode(code) };
      }
    },
    [],
  );

  const resetPasswordForEmail = useCallback(
    async (email: string): Promise<ResetPasswordResult> => {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) {
          const m = error.message.toLowerCase();
          if (m.includes('rate')) {
            return {
              ok: false,
              code: 'rate_limited',
              message: 'Too many reset requests. Please wait a moment.',
            };
          }
          return {
            ok: false,
            code: 'unknown',
            message: 'Unable to send reset email. Please try again.',
          };
        }
        return { ok: true };
      } catch (e) {
        const message = e instanceof Error ? e.message : 'unknown';
        if (message.toLowerCase().includes('network') || message.toLowerCase().includes('fetch')) {
          return { ok: false, code: 'network', message: 'Network error. Check your connection.' };
        }
        return { ok: false, code: 'unknown', message: 'Unable to send reset email.' };
      }
    },
    [],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // onAuthStateChange will move us to 'unauthenticated'.
  }, []);

  const value: AuthContextValue = {
    auth,
    signInWithPassword,
    resetPasswordForEmail,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
